import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import ReactGA from 'react-ga4';
import { MusicMode } from '@/data/worlds';
import { LandingPage } from '@/seo/pages';
import type { SceneLook } from '@/data/scenes';
import { unlockChime } from '@/audio/chime';
import LandingInfo from '@/seo/LandingInfo';

interface YouTubePlayer {
  cueVideoById: (id: string, startSeconds?: number, quality?: string) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  setVolume: (volume: number) => void;
}

interface LobbyProps {
  session: ReturnType<typeof import('@/hooks/useSession').useSession>;
  player?: YouTubePlayer | null;
  loadSong?: (mode: string) => void;
  leaving?: boolean;
  landingPage?: LandingPage;
  othersFocusing?: number;
  looks?: SceneLook[];
  lookId?: string;
  onPickLook?: (id: string) => void;
  myWhere?: string | null; // what others see, e.g. "3pm in London"
  sharePlace?: boolean;
  onToggleSharePlace?: () => void;
}

const VIDEO_IDS: Record<string, string> = {
  lofi: 'rFZHOHl-L8A',
  focus: 'mg7netw1JuM',
  white: 'LD92VXIl8hc',
  forest: 'GwIO1nzuJrw',
  silent: '',
};

const MUSIC_MODES = [
  { id: 'lofi', label: 'lo-fi' },
  { id: 'focus', label: 'focus' },
  { id: 'forest', label: 'nature' },
  { id: 'white', label: 'white noise' },
  { id: 'silent', label: 'silence' },
];

const MIN_DURATION = 5;
const MAX_DURATION = 90;

const Lobby: React.FC<LobbyProps> = ({
  session,
  player,
  loadSong,
  leaving = false,
  landingPage,
  othersFocusing = 0,
  looks,
  lookId,
  onPickLook,
  myWhere,
  sharePlace = true,
  onToggleSharePlace,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickingLook, setPickingLook] = useState(false);
  const currentLook = looks?.find((l) => l.look === lookId) ?? looks?.[0];
  const lobbyRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 900);
    return () => clearTimeout(timer);
  }, []);

  const handleEnter = useCallback(() => {
    if (leaving) return;

    // Landing pages scroll; return to the top so the transition plays in view
    lobbyRef.current?.scrollTo({ top: 0 });

    // Pressing enter is the user gesture browsers need before allowing sound
    unlockChime();

    mixpanel.track('Enter World', {
      world: session.context.world.id,
      duration: session.context.duration,
      musicMode: session.context.musicMode,
    });

    ReactGA.event({
      category: 'Session',
      action: 'Enter World',
      label: session.context.world.id,
    });

    if (session.context.musicMode !== 'silent' && player) {
      const videoId = session.context.musicMode === 'youtube'
        ? session.context.customYoutubeId || VIDEO_IDS.lofi
        : VIDEO_IDS[session.context.musicMode] || VIDEO_IDS.lofi;

      if (videoId) {
        player.cueVideoById(videoId);
        setTimeout(() => player.playVideo(), 500);
      }
    }

    session.enter();
  }, [session, player, leaving]);

  const handleMusicModeChange = useCallback((mode: string) => {
    session.setMusicMode(mode as MusicMode);
    Cookies.set('mode', mode);
    if (loadSong && mode !== 'youtube') {
      loadSong(mode);
    }
  }, [session, loadSong]);

  const { duration, musicMode } = session.context;

  const nudgeDuration = (delta: number) => {
    session.setDuration(Math.min(MAX_DURATION, Math.max(MIN_DURATION, duration + delta)));
  };

  // A custom YouTube mix isn't in the cycle, so cycling from it starts at lo-fi
  const cycleSound = () => {
    const index = MUSIC_MODES.findIndex((m) => m.id === musicMode);
    handleMusicModeChange(MUSIC_MODES[(index + 1) % MUSIC_MODES.length].id);
  };

  const soundLabel = MUSIC_MODES.find((m) => m.id === musicMode)?.label ?? 'your mix';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleEnter();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nudgeDuration(5);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      nudgeDuration(-5);
    }
  };

  return (
    <main
      ref={lobbyRef}
      className={`th-lobby ${leaving ? 'is-leaving' : ''} ${landingPage ? 'has-info' : ''}`}
    >
      <span className="th-mark">
        kaizen<em>flow</em>
      </span>

      <div className="th-portal">
        {landingPage ? (
          <h1 className="th-ask">{landingPage.h1}</h1>
        ) : (
          <label htmlFor="th-intent" className="th-ask">
            what are you here to finish?
          </label>
        )}
        <input
          ref={inputRef}
          id="th-intent"
          type="text"
          value={session.context.task}
          onChange={(e) => session.setTask(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={landingPage ? 'what are you here to finish?' : 'one small thing'}
          aria-label="What are you here to finish?"
          className="th-intent"
          autoComplete="off"
          disabled={leaving}
        />
        <span className="th-intent-line" aria-hidden="true" />
      </div>

      {looks && pickingLook && (
        <div className="th-looks" role="radiogroup" aria-label="Who are you tonight?">
          <span className="th-looks-title">who are you tonight?</span>
          <div className="th-looks-row">
            {looks.map((l) => (
              <button
                key={l.look}
                role="radio"
                aria-checked={l.look === currentLook?.look}
                aria-label={l.lookLabel}
                title={l.lookLabel}
                className={`th-look ${l.look === currentLook?.look ? 'is-current' : ''}`}
                onClick={() => {
                  onPickLook?.(l.look);
                  setPickingLook(false);
                }}
              >
                <img src={l.portrait} alt="" />
              </button>
            ))}
          </div>
          <span className="th-looks-name">{currentLook?.lookLabel}</span>
          {myWhere && onToggleSharePlace && (
            <p className="th-share-place">
              others see: {sharePlace ? myWhere : 'no place or time'}
              <button onClick={onToggleSharePlace}>{sharePlace ? 'hide' : 'show'}</button>
            </p>
          )}
        </div>
      )}

      <nav className="th-meta" aria-label="Session setup">
        {currentLook && (
          <>
            <button
              className={`th-you ${pickingLook ? 'is-open' : ''} ${lookId ? '' : 'is-new'}`}
              onClick={() => setPickingLook((v) => !v)}
              aria-expanded={pickingLook}
              aria-label={`You: ${currentLook.lookLabel}. Change how you look`}
              title="change how you look"
            >
              <span className="th-look th-look-current">
                <img src={currentLook.portrait} alt="" />
              </span>
              <span className="th-you-label">you</span>
            </button>
            <span className="th-sep" aria-hidden="true" />
          </>
        )}
        <div className="th-dial">
          <button
            onClick={() => nudgeDuration(-5)}
            disabled={duration <= MIN_DURATION}
            aria-label="Shorter session"
          >
            −
          </button>
          <span className="th-dial-value">
            {duration}
            <small>min</small>
          </span>
          <button
            onClick={() => nudgeDuration(5)}
            disabled={duration >= MAX_DURATION}
            aria-label="Longer session"
          >
            +
          </button>
        </div>

        <span className="th-sep" aria-hidden="true" />

        <button
          className="th-sound"
          onClick={cycleSound}
          aria-label={`Sound: ${soundLabel}. Click to change`}
        >
          {soundLabel}
        </button>

        <span className="th-sep" aria-hidden="true" />

        <button className="th-enter" onClick={handleEnter}>
          enter <kbd>↵</kbd>
        </button>
      </nav>

      {othersFocusing > 0 && !pickingLook && (
        <p className="th-presence">
          {othersFocusing} {othersFocusing === 1 ? 'person' : 'people'} focusing right now
        </p>
      )}

      <p className="th-foot">
        <kbd>↑</kbd><kbd>↓</kbd> time · no sign-up · just focus
      </p>

      {landingPage && <LandingInfo page={landingPage} onStart={handleEnter} />}
    </main>
  );
};

export default Lobby;
